import { makeStyles } from "@fluentui/react-components";

export const useHealReportViewStyles = makeStyles({
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#0d1117",
        display: "flex",
        flexDirection: "column",
        zIndex: 900,
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 16px",
        backgroundColor: "#010409",
        borderBottom: "1px solid #30363d",
        flexShrink: 0,
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: "24px",
        flexWrap: "wrap",
    },
    headerLogo: {
        width: "24px",
        height: "24px",
        display: "block",
        flexShrink: 0,
    },
    headerTitleGroup: {
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
    },
    headerTitle: {
        fontSize: "16px",
        lineHeight: "20px",
        fontWeight: 600,
        color: "#e6edf3",
    },
    headerMetaItem: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
    },
    headerMetaIcon: {
        width: "20px",
        height: "20px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#d6d6d6",
        fontSize: "16px",
        flexShrink: 0,
    },
    headerMeta: {
        fontSize: "14px",
        lineHeight: "20px",
        color: "#d6d6d6",
    },
    headerMetaLink: {
        fontSize: "14px",
        lineHeight: "20px",
        color: "#479ef5",
        textDecoration: "underline",
        ":hover": { textDecoration: "underline" },
    },
    headerCommit: {
        fontSize: "14px",
        lineHeight: "20px",
        color: "#479ef5",
        textDecoration: "underline",
        fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    headerPr: {
        fontSize: "12px",
        lineHeight: "20px",
        color: "#388bfd",
        textDecoration: "none",
        ":hover": { textDecoration: "underline" },
    },
    closeBtn: {
        border: "none",
        backgroundColor: "transparent",
        color: "#8b949e",
        fontSize: "20px",
        cursor: "pointer",
        padding: "4px 8px",
        borderRadius: "4px",
        ":hover": {
            backgroundColor: "#21262d",
            color: "#e6edf3",
        },
    },
    body: {
        display: "flex",
        flexGrow: 1,
        overflow: "hidden",
        backgroundColor: "#0d1117",
    },
    reportPane: {
        flexGrow: 1,
        overflow: "auto",
        backgroundColor: "#0d1117",
    },
    reportContent: {
        padding: "24px",
        maxWidth: "1200px",
        marginLeft: "auto",
        marginRight: "auto",
    },

    /* ── Summary banner ── */
    summaryBanner: {
        backgroundColor: "#1c2128",
        border: "1px solid #30363d",
        borderRadius: "8px",
        padding: "20px 24px",
        marginBottom: "28px",
    },
    scopeLine: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
        marginBottom: "16px",
        fontSize: "13px",
        color: "#8b949e",
    },
    summaryStats: {
        display: "flex",
        gap: "24px",
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: "16px",
        paddingBottom: "16px",
        borderBottom: "1px solid #30363d",
    },
    summaryStatItem: {
        minWidth: "72px",
    },
    summaryStatDivider: {
        width: "1px",
        height: "51px",
        backgroundColor: "#30363d",
        flexShrink: 0,
    },
    summaryStatHeader: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        marginBottom: "6px",
    },
    summaryStatDot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        flexShrink: 0,
    },
    summaryStatLabel: {
        fontSize: "12px",
        color: "#c9d1d9",
        fontWeight: 500,
    },
    summaryStatCount: {
        fontSize: "22px",
        fontWeight: 700,
        lineHeight: 1,
    },
    summaryLabel: {
        fontSize: "11px",
        fontWeight: 700,
        color: "#8b949e",
        textTransform: "uppercase" as const,
        letterSpacing: "0.06em",
        marginBottom: "8px",
    },
    summaryText: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.7",
        color: "#e6edf3",
    },
    prLink: {
        fontSize: "13px",
        color: "#8b949e",
        marginBottom: "16px",
        "& a": {
            color: "#388bfd",
            textDecoration: "none",
        },
    },

    /* ── Section title (color-coded with chevron) ── */
    sectionHeader: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "16px",
        padding: "0 0 8px 0",
        cursor: "pointer",
        userSelect: "none" as const,
        background: "none",
        border: "none",
        width: "100%",
        font: "inherit",
        color: "inherit",
        textAlign: "left" as const,
    },
    sectionChevron: {
        fontSize: "14px",
        flexShrink: 0,
    },
    sectionTitle: {
        fontSize: "14px",
        fontWeight: 700,
        textTransform: "uppercase" as const,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap" as const,
    },
    sectionDivider: {
        flex: 1,
        height: "1px",
        backgroundColor: "#30363d",
        marginLeft: "4px",
    },

    /* ── Classification cards ── */
    card: {
        backgroundColor: "#161b22",
        border: "1px solid #30363d",
        borderRadius: "10px",
        marginBottom: "12px",
        overflow: "hidden",
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "14px 16px",
        cursor: "pointer",
        gap: "12px",
        ":hover": {
            backgroundColor: "#21262d",
        },
    },
    cardTitleRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        flexGrow: 1,
        minWidth: 0,
    },
    expandIcon: {
        color: "#8b949e",
        fontSize: "12px",
        flexShrink: 0,
        marginTop: "2px",
    },
    testName: {
        fontWeight: 600,
        fontSize: "13px",
        color: "#e6edf3",
        wordBreak: "break-word",
    },
    cardBadges: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flexShrink: 0,
        flexWrap: "wrap",
    },
    badge: {
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
    },
    badgeHeal: {
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: 600,
        backgroundColor: "rgba(56,139,253,0.15)",
        color: "#388bfd",
    },
    badgeScout: {
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: 600,
        backgroundColor: "rgba(63,185,80,0.15)",
        color: "#3fb950",
    },
    verifiedBadge: {
        fontSize: "11px",
        fontWeight: 600,
        color: "#3fb950",
    },
    watchBtn: {
        border: "1px solid #30363d",
        backgroundColor: "#21262d",
        color: "#c9d1d9",
        fontSize: "11px",
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: "6px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        ":hover": {
            backgroundColor: "#30363d",
            color: "#388bfd",
        },
    },
    filePath: {
        fontSize: "12px",
        color: "#8b949e",
        padding: "0 16px 10px 38px",
    },

    /* ── Card expanded details ── */
    cardDetails: {
        padding: "0 16px 16px 38px",
    },
    fixInfo: {
        backgroundColor: "rgba(63,185,80,0.1)",
        borderLeft: "3px solid #3fb950",
        padding: "10px 14px",
        borderRadius: "0 6px 6px 0",
        fontSize: "13px",
        color: "#c9d1d9",
        marginBottom: "12px",
        "& strong": {
            color: "#3fb950",
        },
    },
    devAction: {
        backgroundColor: "rgba(248,81,73,0.1)",
        borderLeft: "3px solid #f85149",
        padding: "10px 14px",
        borderRadius: "0 6px 6px 0",
        fontSize: "13px",
        color: "#c9d1d9",
        marginBottom: "12px",
        "& strong": {
            color: "#f85149",
        },
    },
    evidenceTitle: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#8b949e",
        marginBottom: "8px",
        textTransform: "uppercase",
    },
    evidenceItem: {
        backgroundColor: "#161b22",
        border: "1px solid #21262d",
        borderRadius: "6px",
        padding: "8px 12px",
        marginBottom: "6px",
        fontSize: "12px",
    },
    evidenceArea: {
        fontWeight: 700,
        textTransform: "capitalize",
        marginRight: "8px",
    },
    evidenceDir: {
        display: "inline-block",
        padding: "1px 6px",
        borderRadius: "4px",
        fontSize: "10px",
        fontWeight: 600,
        textTransform: "uppercase",
    },
    evidenceSignal: {
        color: "#c9d1d9",
        marginTop: "4px",
        lineHeight: "1.4",
    },

    /* ── Video modal ── */
    videoModal: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
    },
    videoModalInner: {
        backgroundColor: "#161b22",
        border: "1px solid #30363d",
        borderRadius: "12px",
        width: "min(900px, 90vw)",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
    },
    videoModalHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        borderBottom: "1px solid #21262d",
        backgroundColor: "#0d1117",
    },
    videoModalTitle: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#e6edf3",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    videoModalPlayer: {
        width: "100%",
        maxHeight: "70vh",
    },

    /* ── Loading / error states ── */
    loadingCenter: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: "#8b949e",
        fontSize: "14px",
    },
    errorCenter: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: "#f85149",
        fontSize: "14px",
    },

    /* ── Test card (Figma redesign) ── */
    testCard: {
        backgroundColor: "#161b22",
        border: "1px solid #30363d",
        borderRadius: "8px",
        borderLeftWidth: "3px",
        marginBottom: "12px",
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    },
    testCardTopRow: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
    },
    testCardLeft: {
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        flex: 1,
        minWidth: 0,
    },
    testCardContent: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "4px",
        flex: 1,
        minWidth: 0,
    },
    testCardRight: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flexShrink: 0,
    },
    testCardStatusIcon: {
        fontSize: "16px",
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
    },
    testCardName: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#e6edf3",
        wordBreak: "break-word" as const,
    },
    testCardMeta: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "12px",
        color: "#8b949e",
        flexWrap: "wrap" as const,
    },
    testCardBody: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "6px",
    },
    testCardMetaPath: {
        color: "#8b949e",
    },
    actionBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        border: "1px solid #30363d",
        backgroundColor: "#21262d",
        color: "#c9d1d9",
        fontSize: "11px",
        fontWeight: 500,
        padding: "4px 10px",
        borderRadius: "6px",
        cursor: "pointer",
        whiteSpace: "nowrap" as const,
        ":hover": {
            backgroundColor: "#30363d",
            color: "#e6edf3",
        },
    },

    /* ── Info row (sparkle icon prefix) ── */
    infoRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        fontSize: "13px",
        color: "#c9d1d9",
        lineHeight: "1.55",
        padding: "8px 12px",
        borderRadius: "8px",
    },
    infoRowFixed: {
        backgroundColor: "rgba(84, 176, 84, 0.04)",
    },
    infoRowAppbug: {
        backgroundColor: "rgba(197, 15, 31, 0.05)",
    },
    infoRowFlaky: {
        backgroundColor: "rgba(210, 153, 34, 0.04)",
    },
    infoRowUnclassified: {
        backgroundColor: "rgba(139, 148, 158, 0.04)",
    },
    infoRowIcon: {
        fontSize: "14px",
        flexShrink: 0,
        marginTop: "2px",
    },
    infoRowLabel: {
        fontWeight: 700,
        marginRight: "4px",
    },

    /* ── Reproduced-by collapsible row ── */
    reproducedRow: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "12px",
        color: "#388bfd",
        cursor: "pointer",
        marginTop: "4px",
        ":hover": {
            textDecoration: "underline",
        },
    },

    /* ── Split view: collapsed list + detail pane ── */
    splitBody: {
        display: "flex",
        gap: 0,
        height: "100%",
        minHeight: 0,
    },
    collapsedList: {
        width: "300px",
        flexShrink: 0,
        borderRight: "1px solid #30363d",
        overflowY: "auto",
        padding: "16px 12px",
        boxSizing: "border-box",
    },
    collapsedSection: {
        marginBottom: "20px",
    },
    collapsedSectionHeader: {
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: "#8b949e",
        marginBottom: "8px",
        padding: "0 4px",
    },
    collapsedRow: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 10px",
        borderLeft: "3px solid transparent",
        cursor: "pointer",
        borderRadius: "4px",
        marginBottom: "2px",
        ":hover": {
            backgroundColor: "#161b22",
        },
    },
    collapsedRowSelected: {
        backgroundColor: "#1f2937",
        ":hover": {
            backgroundColor: "#1f2937",
        },
    },
    collapsedRowIcon: {
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        fontSize: "14px",
    },
    collapsedRowName: {
        fontSize: "13px",
        fontWeight: 500,
        color: "#e6edf3",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
        minWidth: 0,
    },
    detailPane: {
        flex: 1,
        minWidth: 0,
        overflowY: "auto",
        padding: "24px 32px",
        boxSizing: "border-box",
    },
    detailHeader: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "16px",
    },
    detailTitleBlock: {
        flex: 1,
        minWidth: 0,
    },
    detailTitleRow: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "6px",
    },
    detailTitle: {
        fontSize: "16px",
        fontWeight: 600,
        color: "#e6edf3",
    },
    detailMetaRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        fontSize: "12px",
        color: "#8b949e",
        flexWrap: "wrap",
    },
    detailMetaPath: {
        color: "#8b949e",
        textDecoration: "none",
        ":hover": {
            textDecoration: "underline",
            color: "#58a6ff",
        },
    },
    detailViewDiffBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 10px",
        fontSize: "12px",
        color: "#58a6ff",
        backgroundColor: "#21262d",
        border: "1px solid #30363d",
        borderRadius: "6px",
        textDecoration: "none",
        cursor: "pointer",
        ":hover": {
            backgroundColor: "#30363d",
        },
    },
    detailHeaderActions: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexShrink: 0,
    },
    detailCloseBtn: {
        background: "transparent",
        border: "none",
        color: "#8b949e",
        cursor: "pointer",
        padding: "6px",
        borderRadius: "4px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "16px",
        ":hover": {
            backgroundColor: "#21262d",
            color: "#e6edf3",
        },
    },
    detailDescription: {
        fontSize: "13px",
        lineHeight: 1.6,
        color: "#c9d1d9",
        marginTop: "16px",
    },
    detailCallout: {
        marginTop: "16px",
    },
    detailTabs: {
        display: "flex",
        borderBottom: "1px solid #30363d",
        marginTop: "20px",
        gap: "4px",
    },
    detailTab: {
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: 500,
        color: "#8b949e",
        background: "transparent",
        border: "none",
        borderBottom: "2px solid transparent",
        cursor: "pointer",
        marginBottom: "-1px",
        ":hover": {
            color: "#e6edf3",
        },
    },
    detailTabActive: {
        color: "#e6edf3",
        borderBottom: "2px solid #388bfd",
    },
    detailVideoWrap: {
        backgroundColor: "#0d1117",
        marginTop: "16px",
        borderRadius: "8px",
        border: "1px solid #30363d",
        overflow: "hidden",
    },
    detailVideo: {
        width: "100%",
        display: "block",
        backgroundColor: "#000",
    },
    detailEmptyVideo: {
        padding: "40px 16px",
        textAlign: "center",
        color: "#8b949e",
        fontSize: "13px",
    },
});
